import React, { useState } from 'react';
import { Card, Row, Col, Collapse, Alert, Table, Tag, Button, Modal } from 'antd';
import { motion } from 'framer-motion';
import { 
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  CalculatorOutlined,
  DollarOutlined,
  HomeOutlined,
  GlobalOutlined,
  ThunderboltOutlined,
  SafetyOutlined
} from '@ant-design/icons';

const { Panel } = Collapse;

interface RequirementItem {
  id: number;
  title: string;
  content: string;
  type: 'warning' | 'info' | 'error';
  icon: React.ReactNode;
}

const requirements: RequirementItem[] = [
  {
    id: 1,
    title: '最低计费重量',
    content: '整票平均不足10kg按照10kg收费',
    type: 'warning',
    icon: <CalculatorOutlined />
  },
  {
    id: 2,
    title: '包装要求',
    content: '纸箱外面不能缠透明胶膜（如果客户需要做防水包装，可以建议客户用透明胶带缠满外箱），蛇皮袋，编织袋',
    type: 'warning',
    icon: <WarningOutlined />
  },
  {
    id: 3,
    title: '重量限制',
    content: '单箱实重需要小于22.5kg，22.5kg<=实重<40kg，加收超重费250RMB/箱；单箱大于40kg的拒收',
    type: 'error',
    icon: <ExclamationCircleOutlined />
  },
  {
    id: 4,
    title: '尺寸限制',
    content: '最长边需要小于121cm，或者次长边需要小于76cm；周长需要小于265cm。121cm<=最长边<200cm，将加收超尺寸附加费145RMB/箱；200cm<=最长边，将加收1300RMB/箱。265cm<=周长<330cm，将加收超尺寸附加费145RMB/箱；330cm<=周长<400cm，将加收1300RMB/箱',
    type: 'error',
    icon: <ExclamationCircleOutlined />
  },
  {
    id: 5,
    title: '拒收标准',
    content: '拒收标准：周长超400cm；单箱实重大于40kg',
    type: 'error',
    icon: <ExclamationCircleOutlined />
  },
  {
    id: 6,
    title: '地址费用',
    content: '商业地址免附加费，私人住宅地址加收25RMB/箱',
    type: 'info',
    icon: <HomeOutlined />
  },
  {
    id: 7,
    title: '偏远地区费用',
    content: '偏远地区附加费RMB20/箱，超偏远地区附加费RMB30/箱；超级超级偏远RMB150/箱',
    type: 'info',
    icon: <GlobalOutlined />
  },
  {
    id: 8,
    title: '报关费用',
    content: '出口报关费RMB300/票，续页费RMB50/页',
    type: 'info',
    icon: <DollarOutlined />
  },
  {
    id: 9,
    title: '磁检费用',
    content: '磁检费RMB350/票，单票超出50件的，超出件数加收RMB1.5/件；如果是带磁性的走普货标快特快渠道需要加收此费用，走带电渠道无需加收',
    type: 'warning',
    icon: <ThunderboltOutlined />
  },
  {
    id: 10,
    title: '带电产品',
    content: '带电产品（内置电池，配套电池）都是走带电渠道',
    type: 'warning',
    icon: <ThunderboltOutlined />
  },
  {
    id: 11,
    title: '包税渠道税率',
    content: '包税渠道会因为每个产品对应的税率不一样，有些高税率的产品会有附加费，需要拿询价要素在318群里和周蜜确认',
    type: 'info',
    icon: <DollarOutlined />
  },
  {
    id: 12,
    title: '分区价格',
    content: '美国的报价根据邮编不同，分为美西（8、9开头），美中（4-7开头），美东（0-3开头），不同的分区价格不一样，整体来说美西最便宜，美东最贵',
    type: 'info',
    icon: <GlobalOutlined />
  }
];

const chargeCalculationData = [
  {
    key: '1',
    item: '实重(KGS)',
    example1: '16.770',
    example2: '13.390',
    example3: '30.160'
  },
  {
    key: '2',
    item: '长(CM)',
    example1: '33.000',
    example2: '38.000',
    example3: '-'
  },
  {
    key: '3',
    item: '宽(CM)',
    example1: '28.000',
    example2: '30.000',
    example3: '-'
  },
  {
    key: '4',
    item: '高(CM)',
    example1: '26.000',
    example2: '26.000',
    example3: '-'
  },
  {
    key: '5',
    item: '周长(CM)',
    example1: '141.000',
    example2: '150.000',
    example3: '-'
  },
  {
    key: '6',
    item: '体积(CBM)',
    example1: '0.0240',
    example2: '0.0296',
    example3: '0.0537'
  },
  {
    key: '7',
    item: '体积重(KGS)',
    example1: '4.004',
    example2: '4.940',
    example3: '8.944'
  }
];

const chargeColumns = [
  {
    title: '入库材质',
    dataIndex: 'item',
    key: 'item',
    width: 120,
  },
  {
    title: '示例1',
    dataIndex: 'example1',
    key: 'example1',
    width: 100,
  },
  {
    title: '示例2',
    dataIndex: 'example2',
    key: 'example2',
    width: 100,
  },
  {
    title: '示例3',
    dataIndex: 'example3',
    key: 'example3',
    width: 100,
    render: (text: string) => text === '30.160' ? <Tag color="red">{text}</Tag> : text
  }
];

const ShippingRequirements: React.FC = () => {
  const [calculatorVisible, setCalculatorVisible] = useState(false);

  const getAlertType = (type: string) => {
    switch (type) {
      case 'error': return 'error';
      case 'warning': return 'warning';
      default: return 'info';
    }
  };

  const getIconColor = (type: string) => {
    switch (type) {
      case 'error': return '#ff4d4f';
      case 'warning': return '#faad14';
      default: return '#1890ff';
    }
  };

  return (
    <section id="shipping-requirements" style={{ 
      padding: '80px 0', 
      background: '#f8f9fa'
    }}>
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          style={{ textAlign: 'center', marginBottom: '60px' }}
        >
          <h2 className="display-medium" style={{ 
            marginBottom: '24px',
            color: '#1d1d1f',
          }}>
            318空派专线接货注意事项
          </h2>
          <p className="body-large" style={{ 
            color: '#86868B', 
            maxWidth: '800px', 
            margin: '0 auto',
            fontSize: '1.125rem'
          }}>
            详细了解接货要求和限制条件，确保您的货物顺利发运
          </p>
        </motion.div>

        {/* 询价要素 */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          style={{ marginBottom: '40px' }}
        >
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <InfoCircleOutlined style={{ color: '#1890ff' }} />
                <span>318询价要素</span>
              </div>
            }
            style={{
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
            }}
          >
            <Alert
              message="询价必需信息"
              description="品名，重量数据，收货人地址或者邮编，是否带电带磁节牌，图片和海关编码（最好有，如果客户没有给的话，也可以根据品名先搜索一下对应的图片和海关编码）"
              type="info"
              showIcon
              style={{ marginBottom: '24px' }}
            />

            <Row gutter={[24, 24]}>
              <Col xs={24} lg={12}>
                <div style={{ 
                  background: '#f0f9ff', 
                  padding: '20px', 
                  borderRadius: '12px',
                  border: '1px solid #91d5ff'
                }}>
                  <h4 style={{ color: '#1890ff', marginBottom: '16px', display: 'flex', alignItems: 'center' }}>
                    <CalculatorOutlined style={{ marginRight: '8px' }} />
                    计费重量算法
                  </h4>
                  <div style={{ fontSize: '14px', lineHeight: '1.6', color: '#666' }}>
                    <p><strong>材积/6000计算</strong></p>
                    <p>整票总材积和总实重取大者为计费重，计费重进位取整。</p>
                    <p style={{ margin: 0 }}>
                      <span style={{ color: '#fa541c' }}>例：</span>
                      共2件收货实重: 30.160KG 收货体重量: 20.000KG 计费重/体积: 31.000KG
                    </p>
                  </div>
                  
                  <Button 
                    type="primary" 
                    size="small"
                    style={{ marginTop: '12px' }}
                    onClick={() => setCalculatorVisible(true)}
                  >
                    查看计算示例
                  </Button>
                </div>
              </Col>

              <Col xs={24} lg={12}>
                <div style={{ 
                  background: '#fff2e8', 
                  padding: '20px', 
                  borderRadius: '12px',
                  border: '1px solid #ffbb96'
                }}>
                  <h4 style={{ color: '#fa541c', marginBottom: '16px', display: 'flex', alignItems: 'center' }}>
                    <ExclamationCircleOutlined style={{ marginRight: '8px' }} />
                    重要提醒
                  </h4>
                  <div style={{ fontSize: '14px', lineHeight: '1.6', color: '#666' }}>
                    <p>• 准确提供货物信息至关重要</p>
                    <p>• 包装必须符合国际空运标准</p>
                    <p>• 带电产品需要走专门的带电渠道</p>
                    <p style={{ margin: 0 }}>• 超重超尺寸货物会产生额外费用</p>
                  </div>
                </div>
              </Col>
            </Row>
          </Card>
        </motion.div>

        {/* 详细注意事项 */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <Card
            title="详细接货注意事项"
            style={{
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
            }}
          >
            <Collapse accordion>
              {requirements.map((req, index) => (
                <Panel 
                  key={req.id}
                  header={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ color: getIconColor(req.type) }}>
                        {req.icon}
                      </span>
                      <span style={{ fontWeight: '600' }}>
                        {req.id}. {req.title}
                      </span>
                      <Tag color={req.type === 'error' ? 'red' : req.type === 'warning' ? 'orange' : 'blue'}>
                        {req.type === 'error' ? '严格限制' : req.type === 'warning' ? '重要提醒' : '费用说明'}
                      </Tag>
                    </div>
                  }
                >
                  <Alert
                    message={req.content}
                    type={getAlertType(req.type)}
                    showIcon
                    style={{ backgroundColor: req.type === 'error' ? '#fff2f0' : req.type === 'warning' ? '#fffbe6' : '#f6ffed' }}
                  />
                </Panel>
              ))}
            </Collapse>

            <div style={{ 
              marginTop: '32px', 
              padding: '20px', 
              background: '#f0f9ff', 
              borderRadius: '12px',
              border: '1px solid #91d5ff'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <SafetyOutlined style={{ color: '#1890ff', fontSize: '18px' }} />
                <span style={{ fontWeight: '600', color: '#1890ff', fontSize: '16px' }}>
                  温馨提示
                </span>
              </div>
              <div style={{ color: '#666', fontSize: '14px', lineHeight: '1.6' }}>
                <p>• 请在发货前仔细核对以上所有要求，避免产生额外费用</p>
                <p>• 如有特殊情况，请提前联系客服确认</p>
                <p style={{ margin: 0 }}>• 我们提供专业的包装建议和优化方案</p>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* 计算示例模态框 */}
        <Modal
          title="计费重量计算示例"
          open={calculatorVisible}
          onCancel={() => setCalculatorVisible(false)}
          footer={[
            <Button key="close" onClick={() => setCalculatorVisible(false)}>
              关闭
            </Button>
          ]}
          width={600}
        >
          <Table
            columns={chargeColumns}
            dataSource={chargeCalculationData}
            pagination={false}
            size="small"
            style={{ marginBottom: '16px' }}
          />
          <Alert
            message="计算说明"
            description="红色标注的30.160KG为最终计费重量，因为实重(30.160)大于体积重(8.944)，所以取实重作为计费重量，最终按31KG计费（进位取整）。"
            type="info"
            showIcon
          />
        </Modal>
      </div>
    </section>
  );
};

export default ShippingRequirements;

